import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Select,
  useToast,
  List,
  ListItem,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Text,
  Divider,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Checkbox,
  Spinner,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import { FaCheckCircle } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import axiosInstance from "../../../configs/axios.config";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Loading } from "../../../components/Loading";
import LabelInput from "../../../components/Input";
import InputWithSuggestionSimple from "../../../components/InputWithSuggestionSimple";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const BASE_URL = import.meta.env.VITE_API_URL || "https://api.travelprym.in";
const INPUT_HEIGHT = "40px";

// Validation Helper Functions
const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const normalizeIndianMobile = (value) => {
  if (!value) return "";
  let digits = value.replace(/\D/g, "");
  if (digits.startsWith("91") && digits.length === 12) {
    digits = digits.slice(2);
  }
  return digits;
};

const isValidMobile = (value) => {
  const mobile = normalizeIndianMobile(value);
  return /^[6-9]\d{9}$/.test(mobile);
};

const isValidLanguage = (value) => {
  // No numerics allowed
  return /^[a-zA-Z\s,]*$/.test(value);
};

const isValidGSTIN = (value) => {
  return /^[0-9A-Z]{15}$/.test(value);
};

function AutocompleteInput({
  label,
  name,
  value,
  onChange,
  onSelectSuggestion,
  placeholder,
  isRequired,
  maxLength,
  error,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchCities = async (query) => {
    if (!query) return setSuggestions([]);

    setLoading(true);
    try {
      const resp = await axiosInstance.get(`${BASE_URL}/management/getplacesuggestions`, {
        params: { 
          input: query,
          filter: "cities",
          country: "in"
        },
      });
      setSuggestions(resp.data?.data?.map((c) => c.description || c.city || c) || []);
      setIsOpen(true);
    } catch (error) {
      console.error("Error fetching cities:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(
    (() => {
      let timer;
      return (query) => {
        clearTimeout(timer);
        timer = setTimeout(() => fetchCities(query), 300);
      };
    })(),
    []
  );

  const handleChange = (e) => {
    onChange(e);
    debouncedFetch(e.target.value);
  };

  return (
    <Box position="relative">
      <Text fontSize="sm" fontWeight="500" mb={1}>
        {label} {isRequired && <span style={{ color: "red" }}>*</span>}
      </Text>

      <Input
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        h={INPUT_HEIGHT}
        borderRadius="md"
        bg="white"
        type="text"
        maxLength={maxLength}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        borderColor={error ? "red.500" : undefined}
      />

      {error && (
        <Text fontSize="sm" color="red.500" mt={1}>
          {error}
        </Text>
      )}

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          zIndex={10}
          maxH="200px"
          overflowY="auto"
        >
          {loading && (
            <Flex justify="center" p={2}>
              <Spinner size="sm" />
            </Flex>
          )}

          {!loading && suggestions.length > 0 && (
            <List>
              {suggestions.map((city, idx) => (
                <ListItem
                  key={idx}
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: "gray.100" }}
                  onMouseDown={() => {
                    onSelectSuggestion(name, city);
                    setIsOpen(false);
                  }}
                >
                  {city}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
}

const CustomBooking = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [vehicleSegments, setVehicleSegments] = useState([]);
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [pricingType, setPricingType] = useState("fixed");
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [whatsappSameAsMobile, setWhatsappSameAsMobile] = useState(true);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [isCorporate, setIsCorporate] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [customerForm, setCustomerForm] = useState({
    name: "",
    email: "",
    mobile: "",
    whatsapp: "",
    city: "",
    gender: "",
    language: "",
    comments: "",
  });
  const [corporateDetails, setCorporateDetails] = useState({
    companyName: "",
    gstNo: "",
  });
  const [formData, setFormData] = useState({
    vehicleSegment: "",
    pickupLocation: "",
    dropLocation: "",
    pickupDateTime: "",
    returnDateTime: "",
    distance: "",
    customer: "",
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    customerWhatsapp: "",
    fixedRate: "",
    ratePerKm: "",
    driverAllowancePerDay: "",
    nightCharges: "",
    tollTax: "",
    addonCharges: "",
    notes: "",
  });

  useEffect(() => {
    fetchVehicleSegments();
    fetchCustomers();
  }, []);

  const toDateTimeLocal = (value) => {
    if (!value) return "";
    const date = new Date(value);
    const pad = (num) => String(num).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Helper function to convert DD/MM/YYYY and HH:MM (24h) to datetime-local format
  const convertToDateTimeLocal = (dateStr, timeStr) => {
    if (!dateStr) return "";
    
    try {
      // Parse DD/MM/YYYY format
      const [day, month, year] = dateStr.split("/").map(Number);
      let hours = 0;
      let minutes = 0;

      // Parse time if provided (handles both 24h and 12h formats)
      if (timeStr) {
        if (timeStr.includes(" ")) {
          // 12h format with AM/PM
          const [time, period] = timeStr.split(" ");
          let [hh, mm] = time.split(":").map(Number);
          if (period === "PM" && hh !== 12) hh += 12;
          if (period === "AM" && hh === 12) hh = 0;
          hours = hh;
          minutes = mm;
        } else {
          // 24h format
          const [hh, mm] = timeStr.split(":").map(Number);
          hours = hh;
          minutes = mm;
        }
      }

      const date = new Date(year, month - 1, day, hours, minutes);
      const pad = (num) => String(num).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    } catch (error) {
      console.error("Error converting date/time:", error);
      return "";
    }
  };

  // Prefill form with data from Results page (via location.state)
  useEffect(() => {
    if (!isEdit && location.state) {
      const { pickupLocation, dropLocation, pickupDate, pickupTime, vehicleSegmentName } = location.state;
      
      // Find vehicle segment ID by name
      let vehicleSegmentId = "";
      if (vehicleSegmentName && vehicleSegments.length > 0) {
        const matchedSegment = vehicleSegments.find(
          (seg) => seg.segmentName === vehicleSegmentName
        );
        vehicleSegmentId = matchedSegment?._id || "";
      }

      // Convert date and time to datetime-local format
      const pickupDateTime = convertToDateTimeLocal(pickupDate, pickupTime);

      setFormData((prev) => ({
        ...prev,
        pickupLocation: pickupLocation || prev.pickupLocation,
        dropLocation: dropLocation || prev.dropLocation,
        pickupDateTime: pickupDateTime || prev.pickupDateTime,
        vehicleSegment: vehicleSegmentId || prev.vehicleSegment,
      }));
    }
  }, [location.state, vehicleSegments, isEdit]);

  useEffect(() => {
    if (!isEdit) return;

    const fetchBooking = async () => {
      try {
        setLoadingBooking(true);
        const res = await axiosInstance.get(`${BASE_URL}/management/custom-booking`, {
          params: { id },
        });

        const booking = res.data?.data || res.data;
        const pricing = booking?.pricingInputs || {};
        const pricingTypeValue = booking?.pricingType || "fixed";

        setPricingType(pricingTypeValue);
        setFormData((prev) => ({
          ...prev,
          vehicleSegment: booking?.vehicleSegment?._id || "",
          pickupLocation: booking?.pickupLocation || "",
          dropLocation: booking?.dropLocation || "",
          pickupDateTime: toDateTimeLocal(booking?.pickupDateTime),
          returnDateTime: toDateTimeLocal(booking?.returnDateTime),
          distance: pricing?.distance || "",
          customer: booking?.customer?._id || "",
          customerName: booking?.customer?.name || "",
          customerPhone: booking?.customer?.mobile || "",
          customerEmail: booking?.customer?.email || "",
          customerWhatsapp: booking?.customer?.whatsapp || booking?.customer?.mobile || "",
          fixedRate: pricing?.baseRate || "",
          ratePerKm: pricing?.perKmRate || "",
          driverAllowancePerDay: pricing?.driverAllowance || "",
          nightCharges: pricing?.nightCharges || "",
          tollTax: pricing?.tollTax || "",
          addonCharges: pricing?.additionalCharges || "",
          notes: booking?.notes || "",
        }));

        // Load customer form details
        setCustomerForm({
          name: booking?.customer?.name || "",
          email: booking?.customer?.email || "",
          mobile: booking?.customer?.mobile || "",
          whatsapp: booking?.customer?.whatsapp || booking?.customer?.mobile || "",
          city: booking?.customer?.city || "",
          gender: booking?.customer?.gender || "",
          language: booking?.customer?.language || "",
          comments: booking?.customer?.comments || "",
        });

        // Load corporate details if applicable
        if (booking?.customer?.isCorporate) {
          setIsCorporate(true);
          setCorporateDetails({
            companyName: booking?.customer?.corporateDetails?.companyName || "",
            gstNo: booking?.customer?.corporateDetails?.gstNo || "",
          });
        } else {
          setIsCorporate(false);
          setCorporateDetails({
            companyName: "",
            gstNo: "",
          });
        }

        setCustomerSearch(booking?.customer?.name || "");
        setWhatsappSameAsMobile(
          (booking?.customer?.whatsapp || booking?.customer?.mobile || "") ===
            (booking?.customer?.mobile || "")
        );
      } catch (error) {
        console.error("Error fetching custom booking:", error);
        toast({
          title: "Error",
          description: "Failed to fetch booking details",
          status: "error",
        });
      } finally {
        setLoadingBooking(false);
      }
    };

    fetchBooking();
  }, [id, isEdit, toast]);

  // Fetch vehicle details when a segment is selected
  useEffect(() => {
    if (!formData.vehicleSegment) {
      setSelectedVehicleDetails(null);
      return;
    }

    const selectedSegment = vehicleSegments.find(
      (v) => v._id === formData.vehicleSegment
    );
    if (selectedSegment) {
      setSelectedVehicleDetails({
        passengerCapacity: selectedSegment.passengerCapacity,
        luggageCapacity: selectedSegment.luggageCapacity,
        fuelTypes: selectedSegment.fuelTypes,
      });
    }
  }, [formData.vehicleSegment, vehicleSegments]);
  const fetchVehicleSegments = async () => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/management/vehicle-segments`);
      setVehicleSegments(response.data.data || []);
    } catch (error) {
      console.error("Error fetching vehicle segments:", error);
      toast({
        title: "Error",
        description: "Failed to fetch vehicle segments",
        status: "error",
      });
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/management/customers`);
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const updated = { ...p, [name]: value };
      if (name === "customerPhone" && whatsappSameAsMobile) {
        updated.customerWhatsapp = value;
      }
      return updated;
    });
    
    // Clear error for this field when user starts typing/changing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleCustomerSearchChange = async (e) => {
    const value = e.target.value;
    setCustomerSearch(value);

    if (value && value.trim()) {
      try {
        const response = await axiosInstance.get(
          `${BASE_URL}/management/customers`,
          {
            params: { search: value },
          }
        );
        setFilteredCustomers(response.data.data || []);
        setShowCustomerDropdown(true);
      } catch (error) {
        console.error("Error searching customers:", error);
        setFilteredCustomers([]);
      }
    } else {
      setFilteredCustomers([]);
      setShowCustomerDropdown(false);
    }
  };

  const onSelectSuggestion = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  const selectCustomer = (customer) => {
    const phone = customer.mobile || "";
    const email = customer.email || customer.emailAddress || "";
    const whatsapp = customer.whatsapp || phone || "";

    setCustomerForm({
      name: customer.name || "",
      email: email,
      mobile: phone,
      whatsapp: whatsapp,
      city: customer.city || "",
      gender: customer.gender || "",
      language: customer.language || "",
      comments: customer.comments || "",
    });

    setFormData((p) => ({
      ...p,
      customer: customer._id,
      customerName: customer.name || "",
      customerPhone: phone,
      customerEmail: email,
      customerWhatsapp: whatsapp,
    }));

    // Handle corporate details
    if (customer.isCorporate && customer.corporateDetails) {
      setIsCorporate(true);
      setCorporateDetails({
        companyName: customer.corporateDetails.companyName || "",
        gstNo: customer.corporateDetails.gstNo || "",
      });
    } else {
      setIsCorporate(false);
      setCorporateDetails({
        companyName: "",
        gstNo: "",
      });
    }

    setCustomerSearch(customer.name || "");
    setShowCustomerDropdown(false);
    setFilteredCustomers([]);
    setIsEditingCustomer(false);
  };

  const handleCustomerFormChange = (field, value) => {
    setCustomerForm((prev) => ({
      ...prev,
      [field]: value,
      // auto sync whatsapp if checkbox is ON
      ...(field === "mobile" && whatsappSameAsMobile
        ? { whatsapp: value }
        : {}),
    }));
    setIsEditingCustomer(true);
    
    // Clear error for this field
    let errorField = field;
    if (field === "name") errorField = "customerName";
    if (field === "mobile") errorField = "customerMobile";
    
    if (formErrors[errorField]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[errorField];
        return updated;
      });
    }
  };

  // Auto-calculate distance when both locations are selected
  useEffect(() => {
    if (!formData.pickupLocation || !formData.dropLocation) {
      if (formData.distance) {
        setFormData((p) => ({ ...p, distance: "" }));
      }
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setCalculatingDistance(true);
        const res = await axiosInstance.post(`${BASE_URL}/management/calculate-distance`, {
          from: formData.pickupLocation,
          to: formData.dropLocation,
        });

        setFormData((p) => ({
          ...p,
          distance: res.data.data.distance,
        }));
      } catch (error) {
        console.error("Error calculating distance:", error);
        setFormData((p) => ({ ...p, distance: "" }));
      } finally {
        setCalculatingDistance(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [formData.pickupLocation, formData.dropLocation]);

  // Validation function
  const validateForm = () => {
    const errors = {};

    // Vehicle segment validation
    if (!formData.vehicleSegment) {
      errors.vehicleSegment = "Vehicle segment is required";
    }

    // Location validation
    if (!formData.pickupLocation) {
      errors.pickupLocation = "Pickup location is required";
    }
    if (!formData.dropLocation) {
      errors.dropLocation = "Drop location is required";
    }

    // Date/Time validation
    if (!formData.pickupDateTime) {
      errors.pickupDateTime = "Pickup date & time is required";
    }

    if (!formData.returnDateTime) {
      errors.returnDateTime = "Return date & time is required";
    }

    // Check if return date/time is not smaller than pickup date/time
    if (formData.pickupDateTime && formData.returnDateTime) {
      const pickupDate = new Date(formData.pickupDateTime);
      const returnDate = new Date(formData.returnDateTime);

      if (returnDate <= pickupDate) {
        errors.returnDateTime = "Return date & time must be after pickup date & time";
      }
    }

    // Distance validation
    // if (!formData.distance) {
    //   errors.distance = "Please calculate distance first";
    // }

    // Customer form validation
    if (!customerForm.name.trim()) {
      errors.customerName = "Customer name is required";
    }

    if (!customerForm.mobile.trim()) {
      errors.customerMobile = "Customer mobile number is required";
    } else if (!isValidMobile(customerForm.mobile)) {
      errors.customerMobile = "Enter valid 10-digit Indian mobile number";
    }

    if (!customerForm.email.trim()) {
      errors.customerEmail = "Email is required";
    } else if (!isValidEmail(customerForm.email)) {
      errors.customerEmail = "Invalid email format";
    }

    if (!customerForm.whatsapp.trim()) {
      errors.customerWhatsapp = "WhatsApp number is required";
    } else if (!isValidMobile(customerForm.whatsapp)) {
      errors.customerWhatsapp = "Enter valid 10-digit Indian WhatsApp number";
    }

    if (!customerForm.gender.trim()) {
      errors.customerGender = "Gender is required";
    }

    if (customerForm.language.trim() && !isValidLanguage(customerForm.language)) {
      errors.customerLanguage = "Language field cannot contain numbers";
    }

    if (!customerForm.city.trim()) {
      errors.customerCity = "City is required";
    }

    // Validate corporate details if corporate
    if (isCorporate) {
      if (!corporateDetails.companyName.trim()) {
        errors.companyName = "Company name is required";
      }
      if (!corporateDetails.gstNo.trim()) {
        errors.gstNo = "GSTIN is required";
      } else if (!isValidGSTIN(corporateDetails.gstNo)) {
        errors.gstNo = "Invalid GSTIN format (must be 15 alphanumeric characters)";
      }
    }

    // Validate pricing based on pricing type
    if (pricingType === "fixed") {
      if (!formData.fixedRate || formData.fixedRate <= 0) {
        errors.fixedRate = "Final amount is required and must be greater than 0";
      }
    } else if (pricingType === "mileage") {
      if (!formData.ratePerKm || formData.ratePerKm <= 0) {
        errors.ratePerKm = "Rate per KM is required and must be greater than 0";
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    // Validate the form
    const errors = validateForm();
    setFormErrors(errors);

    // If there are errors, show the first error as a toast
    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorMessage = errors[firstErrorKey];
      return toast({
        title: firstErrorMessage,
        status: "error",
        duration: 4000,
      });
    }

    try {
      setSubmitting(true);

      // Handle customer creation or update
      let customerId = formData.customer;
      
      if (!customerId) {
        // Create new customer
        try {
          const customerPayload = {
            name: customerForm.name,
            mobile: customerForm.mobile,
            email: customerForm.email || `customer${Date.now()}@auto.com`,
            whatsapp: customerForm.whatsapp || customerForm.mobile,
            city: customerForm.city || "NA",
            gender: customerForm.gender || "Other",
            language: customerForm.language || "",
            comments: customerForm.comments || "",
            isCorporate,
            type: isCorporate ? "Corporate" : "Individual",
            status: "Active",
          };

          if (isCorporate) {
            customerPayload.companyName = corporateDetails.companyName;
            customerPayload.gstNo = corporateDetails.gstNo;
          }

          const customerRes = await axiosInstance.post(
            `${BASE_URL}/management/customer`,
            customerPayload
          );

          if (customerRes.data?.success && customerRes.data?.data?._id) {
            customerId = customerRes.data.data._id;
            setFormData((prev) => ({ ...prev, customer: customerId }));
            toast({
              title: "Customer created",
              status: "success",
              duration: 2000,
            });
          }
        } catch (err) {
          toast({
            title: "Error creating customer",
            description: err.response?.data?.message || err.message,
            status: "error",
          });
          setSubmitting(false);
          return;
        }
      } else if (isEditingCustomer) {
        // Update existing customer if there are changes
        try {
          const updatePayload = {
            name: customerForm.name,
            email: customerForm.email,
            whatsapp: customerForm.whatsapp,
            city: customerForm.city,
            gender: customerForm.gender,
            language: customerForm.language,
            comments: customerForm.comments,
            isCorporate,
            type: isCorporate ? "Corporate" : "Individual",
          };

          // Only include mobile if it has not changed to avoid conflicts
          if (customerForm.mobile !== formData.customerPhone) {
            updatePayload.mobile = customerForm.mobile;
          }

          if (isCorporate) {
            updatePayload.companyName = corporateDetails.companyName;
            updatePayload.gstNo = corporateDetails.gstNo;
          }

          await axiosInstance.patch(
            `${BASE_URL}/management/customer/${customerId}`,
            updatePayload
          );

          toast({
            title: "Customer updated",
            status: "success",
            duration: 2000,
          });
        } catch (err) {
          console.error("Error updating customer:", err);
          
          if (err.response?.status === 409) {
            toast({
              title: "Cannot update customer",
              description: "Mobile number already registered to another customer",
              status: "error",
            });
            setSubmitting(false);
            return;
          }

          toast({
            title: "Warning",
            description: "Customer update failed but continuing with booking",
            status: "warning",
          });
        }
      }

      const payload = {
        vehicleSegment: formData.vehicleSegment,
        pickupLocation: formData.pickupLocation,
        dropLocation: formData.dropLocation,
        pickupDateTime: formData.pickupDateTime,
        returnDateTime: formData.returnDateTime,
        customer: customerId,
        pricingType: pricingType,
        bookingType: 'custom',
        // Add capacity and fuel type from selected vehicle
        capacity: {
          passengers: selectedVehicleDetails?.passengerCapacity || 0,
          luggage: selectedVehicleDetails?.luggageCapacity || 0,
        },
        fuelType: selectedVehicleDetails?.fuelTypes?.[0] || null,
        pricingInputs: {
          distance: formData.distance,
        },
        notes: formData.notes,
      };

      if (pricingType === "fixed") {
        payload.pricingInputs.baseRate = formData.fixedRate;
      } else {
        payload.pricingInputs.perKmRate = formData.ratePerKm;
        payload.pricingInputs.driverAllowance = formData.driverAllowancePerDay;
        payload.pricingInputs.nightCharges = formData.nightCharges;
        payload.pricingInputs.tollTax = formData.tollTax;
        payload.pricingInputs.additionalCharges = formData.addonCharges || 0;
      }

      if (isEdit) {
        const response = await axiosInstance.patch(
          `${BASE_URL}/management/custom-booking/${id}`,
          payload
        );

        toast({
          title: "Custom Booking Updated",
          description: `Booking ID: ${response.data.data.customBookingID}`,
          status: "success",
          duration: 3000,
        });

        navigate("/bookings");
      } else {
        const response = await axiosInstance.post(
          `${BASE_URL}/management/custom-booking`,
          payload
        );

        toast({
          title: "Custom Booking Created",
          description: `Booking ID: ${response.data.data.customBookingID}`,
          status: "success",
          duration: 3000,
        });

        setFormData((prev) => ({
          ...prev,
          vehicleSegment: "",
          pickupLocation: "",
          dropLocation: "",
          pickupDateTime: "",
          returnDateTime: "",
          distance: "",
          customer: "",
          customerName: "",
          customerPhone: "",
          customerEmail: "",
          customerWhatsapp: "",
          fixedRate: "",
          ratePerKm: "",
          driverAllowancePerDay: "",
          nightCharges: "",
          tollTax: "",
          addonCharges: "",
          notes: "",
        }));
        setCustomerSearch("");
        setWhatsappSameAsMobile(true);
        setPricingType("fixed");
        navigate("/bookings");
      }
    } catch (error) {
      console.error("Error creating/updating booking:", error);
      toast({
        title: isEdit ? "Error updating booking" : "Error creating booking",
        description:
          error.response?.data?.message ||
          (isEdit ? "Failed to update" : "Failed to create"),
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingBooking) {
    return <Loading />;
  }

  return (
    <Box px={6} py={8}>
      <BreadCrumbMaker
        items={[
          { link: "/bookings", title: "Bookings" },
          {
            link: "/bookings/custom-booking",
            title: isEdit ? "Edit Custom Booking" : "Create Custom Booking",
            on: true,
          },
        ]}
      />

      <Box bg="white" p={8} borderRadius="lg" mt={6} shadow="md" mx="auto">
        <Heading fontSize="2xl" mb={6}>
          {isEdit ? "Edit Custom Booking" : "Create Custom Booking"}
        </Heading>

        {/* VEHICLE SELECTION */}
        <Box mb={8}>
          <Heading fontSize="lg" mb={4} color="blue.600">
            1. Select Vehicle
          </Heading>
          <Grid templateColumns="1fr 1fr 1fr 1fr" gap={4}>
            <GridItem>
              <FormControl isInvalid={!!formErrors.vehicleSegment}>
                <FormLabel fontSize="sm" fontWeight="500">
                  Vehicle Segment <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Select
                  h={INPUT_HEIGHT}
                  name="vehicleSegment"
                  value={formData.vehicleSegment}
                  onChange={handleChange}
                  placeholder="Select Vehicle Segment"
                  borderColor={formErrors.vehicleSegment ? "red.500" : undefined}
                >
                  {vehicleSegments && vehicleSegments.length > 0 ? (
                    vehicleSegments.map((v) => (
                      <option key={v._id} value={v._id}>
                        {v.segmentName || v.name || "Unknown"}
                      </option>
                    ))
                  ) : (
                    <option disabled>No vehicles available</option>
                  )}
                </Select>
                {formErrors.vehicleSegment && (
                  <FormErrorMessage fontSize="sm" mt={1}>
                    {formErrors.vehicleSegment}
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>

            {/* Vehicle Details - Always Visible */}
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500">
                  Passenger Capacity
                </FormLabel>
                <Flex
                  h={INPUT_HEIGHT}
                  align="center"
                  px={4}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                >
                  <Text fontSize="md" fontWeight="600" color={selectedVehicleDetails ? "blue.600" : "gray.400"}>
                    {selectedVehicleDetails ? (
                      <>
                        {selectedVehicleDetails.passengerCapacity}{" "}
                        <Text as="span" fontSize="sm" color="gray.500">
                          seats
                        </Text>
                      </>
                    ) : (
                      "_"
                    )}
                  </Text>
                </Flex>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500">
                  Luggage Capacity
                </FormLabel>
                <Flex
                  h={INPUT_HEIGHT}
                  align="center"
                  px={4}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                >
                  <Text fontSize="md" fontWeight="600" color={selectedVehicleDetails ? "green.600" : "gray.400"}>
                    {selectedVehicleDetails ? (
                      <>
                        {selectedVehicleDetails.luggageCapacity}{" "}
                        <Text as="span" fontSize="sm" color="gray.500">
                          units
                        </Text>
                      </>
                    ) : (
                      "_"
                    )}
                  </Text>
                </Flex>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500">
                  Fuel Type
                </FormLabel>
                <Flex
                  h={INPUT_HEIGHT}
                  align="center"
                  px={4}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  gap={2}
                  flexWrap="wrap"
                >
                  {selectedVehicleDetails && selectedVehicleDetails.fuelTypes && selectedVehicleDetails.fuelTypes.length > 0 ? (
                    selectedVehicleDetails.fuelTypes.map((fuel, idx) => (
                      <Text
                        key={idx}
                        fontSize="sm"
                        fontWeight="500"
                        textTransform="capitalize"
                      >
                        {fuel}
                      </Text>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.400">
                      _
                    </Text>
                  )}
                </Flex>
              </FormControl>
            </GridItem>
          </Grid>
        </Box>

        <Divider my={8} />

        {/* LOCATION & TIME SELECTION */}
        <Box mb={8}>
          <Heading fontSize="lg" mb={4} color="blue.600">
            2. Trip Details
          </Heading>
          <Grid templateColumns="repeat(5, 2fr)" gap={4}>
            <GridItem colSpan={1}>
              <AutocompleteInput
                label="Pickup Location"
                name="pickupLocation"
                value={formData.pickupLocation}
                onChange={handleChange}
                onSelectSuggestion={onSelectSuggestion}
                placeholder="Enter Pickup Location"
                isRequired
                maxLength={120}
                error={formErrors.pickupLocation}
              />
            </GridItem>

            <GridItem colSpan={1}>
              <AutocompleteInput
                label="Drop Location"
                name="dropLocation"
                value={formData.dropLocation}
                onChange={handleChange}
                onSelectSuggestion={onSelectSuggestion}
                placeholder="Enter Drop Location"
                isRequired
                maxLength={120}
                error={formErrors.dropLocation}
              />
            </GridItem>

            <GridItem>
              <FormControl isInvalid={!!formErrors.pickupDateTime}>
                <FormLabel fontSize="sm" fontWeight="500">
                  Pickup Date & Time <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Input
                  h={INPUT_HEIGHT}
                  name="pickupDateTime"
                  type="datetime-local"
                  value={formData.pickupDateTime}
                  onChange={handleChange}
                  required
                  borderColor={formErrors.pickupDateTime ? "red.500" : undefined}
                />
                {formErrors.pickupDateTime && (
                  <FormErrorMessage fontSize="sm" mt={1}>
                    {formErrors.pickupDateTime}
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={!!formErrors.returnDateTime}>
                <FormLabel fontSize="sm" fontWeight="500">
                  Return Date & Time <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Input
                  h={INPUT_HEIGHT}
                  name="returnDateTime"
                  type="datetime-local"
                  value={formData.returnDateTime}
                  onChange={handleChange}
                  required
                  borderColor={formErrors.returnDateTime ? "red.500" : undefined}
                  min={formData.pickupDateTime || undefined}
                />
                {formErrors.returnDateTime && (
                  <FormErrorMessage fontSize="sm" mt={1}>
                    {formErrors.returnDateTime}
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={!!formErrors.distance}>
                <FormLabel fontSize="sm" fontWeight="500">
                  Distance
                </FormLabel>
                <Flex
                  h={INPUT_HEIGHT}
                  align="center"
                  px={4}
                  bg="white"
                  border="1px solid"
                  borderColor={formErrors.distance ? "red.500" : "gray.200"}
                  borderRadius="md"
                >
                  {calculatingDistance ? (
                    <Flex align="center" gap={2}>
                      <Spinner size="xs" color="blue.500" />
                      <Text fontSize="sm" color="gray.500">Calculating...</Text>
                    </Flex>
                  ) : (
                    <Text fontSize="md" fontWeight="600" color={formData.distance ? "blue.600" : "gray.400"}>
                      {formData.distance ? `${formData.distance} km` : "--"}
                    </Text>
                  )}
                </Flex>
                {formErrors.distance && (
                  <FormErrorMessage fontSize="sm" mt={1}>
                    {formErrors.distance}
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>
          </Grid>
        </Box>

        <Divider my={8} />

        {/* CUSTOMER SELECTION */}
        <Box mb={8}>
          <Box borderWidth="1px" borderRadius="lg" p={6} bg="white">
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontSize="18px" fontWeight="600">
                Customer Details <span style={{ color: "red" }}>*</span>
              </Text>
              {formData.customer && !isEditingCustomer && (
                <Button
                  colorScheme="blue"
                  size="sm"
                  onClick={() => setIsEditingCustomer(true)}
                >
                  Edit Customer
                </Button>
              )}
            </Flex>

            {/* Search for existing customer */}
            {!formData.customer || isEditingCustomer ? (
              <>
                <FormControl position="relative" w="100%" mb={4}>
                  <FormLabel>Search Customer</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h={INPUT_HEIGHT}>
                      <FiSearch color="#718EBF" fontSize="0.9rem" />
                    </InputLeftElement>
                    <Input
                      name="customerSearch"
                      value={customerSearch}
                      onChange={handleCustomerSearchChange}
                      placeholder="Search by name or phone or email"
                      h={INPUT_HEIGHT}
                      pl="30px"
                      type="text"
                      maxLength={60}
                      onFocus={() => customerSearch && setShowCustomerDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                    />
                  </InputGroup>
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      zIndex={10}
                      maxH="250px"
                      overflowY="auto"
                      mt={1}
                      borderRadius="md"
                    >
                      <List>
                        {filteredCustomers.map((customer) => (
                          <ListItem
                            key={customer._id}
                            px={3}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: "gray.100" }}
                            onMouseDown={() => selectCustomer(customer)}
                          >
                            <Text fontWeight="500">{customer.name}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {customer.mobile || customer.phone || ""}
                            </Text>
                            {customer.email && (
                              <Text fontSize="xs" color="gray.500">
                                {customer.email}
                              </Text>
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </FormControl>

                {/* Customer Form Fields */}
                <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
                  <FormControl isInvalid={!!formErrors.customerName}>
                    <FormLabel>
                      Name <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      value={customerForm.name}
                      onChange={(e) => handleCustomerFormChange("name", e.target.value)}
                      placeholder="Enter Name"
                      h={INPUT_HEIGHT}
                      maxLength={60}
                      borderColor={formErrors.customerName ? "red.500" : undefined}
                    />
                    {formErrors.customerName && (
                      <FormErrorMessage fontSize="sm" mt={1}>
                        {formErrors.customerName}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isInvalid={!!formErrors.customerEmail}>
                    <FormLabel>Email ID <span style={{ color: "red" }}>*</span></FormLabel>
                    <Input
                      value={customerForm.email}
                      onChange={(e) => handleCustomerFormChange("email", e.target.value)}
                      placeholder="Enter Email"
                      h={INPUT_HEIGHT}
                      type="email"
                      maxLength={80}
                      borderColor={formErrors.customerEmail ? "red.500" : undefined}
                    />
                    {formErrors.customerEmail && (
                      <FormErrorMessage fontSize="sm" mt={1}>
                        {formErrors.customerEmail}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isInvalid={!!formErrors.customerMobile}>
                    <FormLabel>
                      Mobile Number <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      value={customerForm.mobile}
                      onChange={(e) => handleCustomerFormChange("mobile", e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter Mobile Number"
                      h={INPUT_HEIGHT}
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      borderColor={formErrors.customerMobile ? "red.500" : undefined}
                    />
                    {formErrors.customerMobile && (
                      <FormErrorMessage fontSize="sm" mt={1}>
                        {formErrors.customerMobile}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isInvalid={!!formErrors.customerWhatsapp}>
                    <FormLabel>
                      WhatsApp Number <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      value={customerForm.whatsapp}
                      onChange={(e) => handleCustomerFormChange("whatsapp", e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter WhatsApp Number"
                      h={INPUT_HEIGHT}
                      isDisabled={whatsappSameAsMobile}
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      borderColor={formErrors.customerWhatsapp ? "red.500" : undefined}
                    />
                    {formErrors.customerWhatsapp && (
                      <FormErrorMessage fontSize="sm" mt={1}>
                        {formErrors.customerWhatsapp}
                      </FormErrorMessage>
                    )}
                    <Checkbox
                      mt={2}
                      size="sm"
                      isChecked={whatsappSameAsMobile}
                      onChange={(e) => {
                        setWhatsappSameAsMobile(e.target.checked);
                        if (e.target.checked) {
                          setCustomerForm((prev) => ({
                            ...prev,
                            whatsapp: prev.mobile,
                          }));
                          // Clear error when auto-filled
                          if (formErrors.customerWhatsapp) {
                            setFormErrors((prev) => {
                              const updated = { ...prev };
                              delete updated.customerWhatsapp;
                              return updated;
                            });
                          }
                        }
                      }}
                    >
                      Same as mobile
                    </Checkbox>
                  </FormControl>

                  <Box>
                    <FormLabel>
                    </FormLabel>
                    <AutocompleteInput
                label="City"
                name="city"
                value={customerForm.city}
                onChange={handleChange}
                onSelectSuggestion={onSelectSuggestion}
                placeholder="Enter city"
                isRequired
                maxLength={120}
                error={formErrors.customerCity}
              />
                   
                  </Box>

                  <FormControl isInvalid={!!formErrors.customerGender}>
                    <FormLabel>
                      Gender <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Select
                      value={customerForm.gender}
                      onChange={(e) => handleCustomerFormChange("gender", e.target.value)}
                      placeholder="Select Gender"
                      h={INPUT_HEIGHT}
                      borderColor={formErrors.customerGender ? "red.500" : undefined}
                    >
                      <option value=""></option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Select>
                    {formErrors.customerGender && (
                      <FormErrorMessage fontSize="sm" mt={1}>
                        {formErrors.customerGender}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isInvalid={!!formErrors.customerLanguage}>
                    <FormLabel>Language</FormLabel>
                    <Input
                      value={customerForm.language}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Remove numbers in real-time
                        const cleaned = value.replace(/\d/g, '');
                        handleCustomerFormChange("language", cleaned);
                      }}
                      placeholder="e.g., English, Hindi"
                      h={INPUT_HEIGHT}
                      maxLength={50}
                      borderColor={formErrors.customerLanguage ? "red.500" : undefined}
                    />
                    {formErrors.customerLanguage && (
                      <FormErrorMessage fontSize="sm" mt={1}>
                        {formErrors.customerLanguage}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl colSpan={2}>
                    <FormLabel>Comments/Notes</FormLabel>
                    <Input
                      value={customerForm.comments}
                      onChange={(e) => handleCustomerFormChange("comments", e.target.value)}
                      placeholder="Enter any comments"
                      h={INPUT_HEIGHT}
                      maxLength={200}
                    />
                  </FormControl>
                </Grid>

                {/* Corporate Details */}
                <Checkbox
                  mb={4}
                  isChecked={isCorporate}
                  onChange={(e) => setIsCorporate(e.target.checked)}
                >
                  <Text fontWeight="500">Corporate Client</Text>
                </Checkbox>
                
                {isCorporate && (
                  <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                    <FormControl isInvalid={!!formErrors.companyName}>
                      <FormLabel>
                        Company Name <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <Input
                        value={corporateDetails.companyName}
                        onChange={(e) => {
                          setCorporateDetails((prev) => ({
                            ...prev,
                            companyName: e.target.value,
                          }));
                          if (formErrors.companyName) {
                            setFormErrors((prev) => {
                              const updated = { ...prev };
                              delete updated.companyName;
                              return updated;
                            });
                          }
                        }}
                        placeholder="Enter Company Name"
                        h={INPUT_HEIGHT}
                        maxLength={100}
                        borderColor={formErrors.companyName ? "red.500" : undefined}
                      />
                      {formErrors.companyName && (
                        <FormErrorMessage fontSize="sm" mt={1}>
                          {formErrors.companyName}
                        </FormErrorMessage>
                      )}
                    </FormControl>

                    <FormControl isInvalid={!!formErrors.gstNo}>
                      <FormLabel>
                        GSTIN <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <Input
                        value={corporateDetails.gstNo}
                        onChange={(e) => {
                          setCorporateDetails((prev) => ({
                            ...prev,
                            gstNo: e.target.value.toUpperCase(),
                          }));
                          if (formErrors.gstNo) {
                            setFormErrors((prev) => {
                              const updated = { ...prev };
                              delete updated.gstNo;
                              return updated;
                            });
                          }
                        }}
                        placeholder="Enter GSTIN"
                        h={INPUT_HEIGHT}
                        maxLength={15}
                        borderColor={formErrors.gstNo ? "red.500" : undefined}
                      />
                      {formErrors.gstNo && (
                        <FormErrorMessage fontSize="sm" mt={1}>
                          {formErrors.gstNo}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  </Grid>
                )}

              </>
            ) : (
              /* Display Selected Customer */
              <Box p={4} bg="gray.50" borderRadius="md">
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Name</Text>
                    <Text fontWeight="500" fontSize="md">{customerForm.name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Mobile</Text>
                    <Text fontWeight="500" fontSize="md">{customerForm.mobile}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Email</Text>
                    <Text fontWeight="500" fontSize="md">{customerForm.email || "-"}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">WhatsApp</Text>
                    <Text fontWeight="500" fontSize="md">{customerForm.whatsapp || "-"}</Text>
                  </Box>
                  {customerForm.city && (
                    <Box>
                      <Text fontSize="sm" color="gray.600">City</Text>
                      <Text fontWeight="500" fontSize="md">{customerForm.city}</Text>
                    </Box>
                  )}
                  {customerForm.gender && (
                    <Box>
                      <Text fontSize="sm" color="gray.600">Gender</Text>
                      <Text fontWeight="500" fontSize="md">{customerForm.gender}</Text>
                    </Box>
                  )}
                  {isCorporate && (
                    <>
                      <Box>
                        <Text fontSize="sm" color="gray.600">Company</Text>
                        <Text fontWeight="500" fontSize="md">{corporateDetails.companyName}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600">GSTIN</Text>
                        <Text fontWeight="500" fontSize="md">{corporateDetails.gstNo}</Text>
                      </Box>
                    </>
                  )}
                </Grid>
              </Box>
            )}
          </Box>
        </Box>

        <Divider my={8} />

        {/* PRICING TYPE */}
        <Box mb={8}>
          <Heading fontSize="lg" mb={4} color="blue.600">
            3. Pricing Type <span style={{ color: "red" }}>*</span>
          </Heading>
          <RadioGroup value={pricingType} onChange={setPricingType}>
            <Stack direction="row" spacing={8}>
              <Radio value="fixed" colorScheme="blue">
                <Text fontWeight="600">Fixed Rate</Text>
              </Radio>
              <Radio value="mileage" colorScheme="blue">
                <Text fontWeight="600">Mileage Based</Text>
              </Radio>
            </Stack>
          </RadioGroup>
        </Box>

        {/* PRICING DETAILS */}
        <Box mb={8}>
          {pricingType === "fixed" ? (
            <Box>
              <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                <GridItem colSpan={1}>
                  <FormControl isInvalid={!!formErrors.fixedRate}>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Final Amount <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      type="number"
                      name="fixedRate"
                      value={formData.fixedRate}
                      onChange={handleChange}
                      placeholder="Enter final booking amount"
                      h={INPUT_HEIGHT}
                      borderColor={formErrors.fixedRate ? "red.500" : undefined}
                    />
                    {formErrors.fixedRate && (
                      <FormErrorMessage fontSize="sm" mt={1}>
                        {formErrors.fixedRate}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>
          ) : (
            <Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isInvalid={!!formErrors.ratePerKm}>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Rate Per KM <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" color="gray.500">
                        ₹
                      </InputLeftElement>
                      <Input
                        type="number"
                        name="ratePerKm"
                        value={formData.ratePerKm}
                        onChange={handleChange}
                        placeholder="/km"
                        h={INPUT_HEIGHT}
                        borderColor={formErrors.ratePerKm ? "red.500" : undefined}
                      />
                    </InputGroup>
                    {formErrors.ratePerKm && (
                      <FormErrorMessage fontSize="sm" mt={1}>
                        {formErrors.ratePerKm}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Driver Allowance/Day
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" color="gray.500">
                        ₹
                      </InputLeftElement>
                      <Input
                        type="number"
                        name="driverAllowancePerDay"
                        value={formData.driverAllowancePerDay}
                        onChange={handleChange}
                        placeholder="Amount per day"
                        h={INPUT_HEIGHT}
                      />
                    </InputGroup>
                  </FormControl>
                </GridItem>

                {/* <GridItem>
                  <LabelInput
                    label="Night Charges"
                    type="number"
                    name="nightCharges"
                    value={formData.nightCharges}
                    onChange={handleChange}
                    placeholder="₹/Night"
                  />
                </GridItem> */}

                {/* <GridItem>
                  <LabelInput
                    label="Toll Tax"
                    type="number"
                    name="tollTax"
                    value={formData.tollTax}
                    onChange={handleChange}
                    placeholder="₹ Toll tax amount"
                  />
                </GridItem> */}

                  <GridItem>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Additional Charges
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" color="gray.500">
                        ₹
                      </InputLeftElement>
                      <Input
                        type="number"
                        name="addonCharges"
                        value={formData.addonCharges}
                        onChange={handleChange}
                        placeholder="Any extra charges"
                        h={INPUT_HEIGHT}
                      />
                    </InputGroup>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>
          )}
        </Box>

        <Divider my={8} />

        {/* NOTES */}
        <Box mb={8}>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500">
              Notes / Comments
            </FormLabel>
            <Input
              as="textarea"
              minH="100px"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any special instructions or comments..."
              maxLength={150}
            />
          </FormControl>
        </Box>

        {/* SUBMIT BUTTON */}
        <Flex justify="flex-end" gap={4}>
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            size="lg"
            onClick={handleSubmit}
            isLoading={submitting}
          >
            {isEdit ? "Update Booking" : "Create Booking"}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export default CustomBooking;
